import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ImportContactsOutlinedIcon from '@mui/icons-material/ImportContactsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import { Sidebar } from 'react-pro-sidebar';
import { Navigation, MenuContainer } from '../../resources/styling/appStyle';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import { useIdentityAuth } from '../../auth';

// Left-nav items, in the order the new Super Admin design lays them out. `match`
// lists the route prefixes that should light up the item (so a school/group detail
// view keeps its parent listing highlighted). `superUserOnly` gates on the role.
const MENU_ITEMS = [
  { label: 'Dashboard', to: '/super-admin-dashboard', Icon: DashboardOutlinedIcon, match: ['/super-admin-dashboard', '/'] },
  { label: 'Super Management', to: '/super-management', Icon: PeopleAltOutlinedIcon, superUserOnly: true },
  { label: 'Audit Log', to: '/audit-log', Icon: FactCheckOutlinedIcon, superUserOnly: true },
  { label: 'Schools', to: '/school-listing', Icon: ImportContactsOutlinedIcon, match: ['/school-listing', '/school-detail'] },
  { label: 'Groups', to: '/group-listing', Icon: PeopleAltOutlinedIcon, match: ['/group-listing', '/group-detail'] },
  { label: 'Account Registration', to: '/account-registrations', Icon: SettingsOutlinedIcon },
  { label: 'Filter Library', to: '/filter-library', Icon: TuneIcon },
  { label: 'Sent Emails', to: '/sent-emails', Icon: MailOutlineIcon },
  { label: 'Process Invoices', to: '/Invoices', Icon: FeedOutlinedIcon },
  { label: 'Upcoming Renewals', to: '/upcoming-renewals', Icon: FactCheckOutlinedIcon },
  { label: 'Subscriptions', to: '/subscription-list', Icon: FactCheckOutlinedIcon },
  { label: 'Email Logs', to: '/sent-emails-logs', Icon: SubjectOutlinedIcon },
];

// The AI Usage drill-in is reachable from three different sections — it is a tab on the Dashboard,
// on School Detail and on Group Detail — so no single `match` entry can own it. Its `scope` query
// param records which one the operator came from, and that is the item that stays lit; without this
// the drill-in lit nothing at all and reading as though it had left the portal.
const AI_USAGE_PATH = '/ai-usage-analytics';
const AI_USAGE_PARENTS = {
  school: '/school-listing',
  group: '/group-listing',
};

function SideMenu() {
  const { pathname, search } = useLocation();
  const { logout, hasRole } = useIdentityAuth();

  const aiUsageParent = React.useMemo(() => {
    if (!(pathname || '').toLowerCase().startsWith(AI_USAGE_PATH)) return null;
    const scope = new URLSearchParams(search).get('scope');
    return AI_USAGE_PARENTS[scope] || '/super-admin-dashboard';
  }, [pathname, search]);

  const isActive = (item) => {
    const targets = item.match || [item.to];
    const path = (pathname || '').toLowerCase();
    if (aiUsageParent) return item.to === aiUsageParent;
    return targets.some((t) => {
      const target = t.toLowerCase();
      return target === '/' ? path === '/' : path.startsWith(target);
    });
  };
  const [toggled, setToggled] = React.useState(false);
  const [, setBreakPoint] = React.useState(window.matchMedia('(max-width: 1024px)').matches);

  return (
    <Sidebar
      className='leftsidebar'
      onBackdropClick={() => setToggled(false)}
      toggled={toggled}
      customBreakPoint='1024px'
      onBreakPoint={setBreakPoint}
    >
      <MenuContainer>
        <Navigation>
          <div className='logo'>
            <img
              alt=''
              src='https://welbee.international/wp-content/uploads/2021/04/welbee_logo_yellow.png'
            ></img>
          </div>
          <div>
            <div className='menu'>
              <MenuList>
                {MENU_ITEMS.filter(
                  (item) => !item.superUserOnly || hasRole('SuperUser')
                ).map((item) => (
                  <MenuItem key={item.to}>
                    <Link to={item.to} className={isActive(item) ? 'active' : ''}>
                      <item.Icon />
                      <span>{item.label}</span>
                    </Link>
                  </MenuItem>
                ))}
              </MenuList>
            </div>

            <div className='menu-logout'>
              <button
                type='button'
                className='logout-btn'
                onClick={(e) => {
                  e.preventDefault();
                  logout();
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </Navigation>
      </MenuContainer>
    </Sidebar>
  );
}

export { SideMenu };
